import React, { useEffect, useState } from "react";
import { useAllProducts } from "../../../../../api/products/products";
import { Select } from "antd";
import PaymentModal from "../PaymentModal";

const ProductInformation = ({ onNext, onPrevious, defaultData, isLoading }) => {
  const [products, setProducts] = useState([
    {
      productName: "",
      quantity: "",
      price: "",
      payableAmount: "",
      discount: "",
      totalAmount: "",
      sku: "",
      isManual: false,
    },
  ]);

  const [dimensions, setDimensions] = useState({
    weight: "",
    length: "",
    breadth: "",
    height: "",
  });

  const [paymentData, setPaymentData] = useState({
    paymentMethods: [],
    totalAmount: 0,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [errors, setErrors] = useState({});
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalDiscount, setTotalDiscount] = useState(0);
  const [isManualTotal, setIsManualTotal] = useState(false);

  const { data: productOptions } = useAllProducts();

  useEffect(() => {
    if (defaultData?.productInfo) setProducts(defaultData.productInfo);
    if (defaultData?.dimensions) setDimensions(defaultData.dimensions);
    if (defaultData?.totalAmount) setTotalAmount(defaultData.totalAmount);
    if (defaultData?.paymentData) setPaymentData(defaultData.paymentData);
  }, [defaultData]);

  useEffect(() => {
    let priceSum = 0;
    let discountSum = 0;
    let payableSum = 0;

    products.forEach((p) => {
      const qty = Number(p.quantity) || 0;
      const price = Number(p.price) || 0;
      const payable = Number(p.payableAmount) || 0;
      const total = qty * price;

      priceSum += total;
      discountSum += total - payable;
      payableSum += payable;
    });

    setTotalPrice(priceSum);
    setTotalDiscount(discountSum);

    if (!isManualTotal) {
      setTotalAmount(payableSum);
    }
  }, [products, isManualTotal]);

  const handleProductChange = (index, e) => {
    const { name, value } = e.target;
    const updated = [...products];
    const currentProduct = updated[index];

    if (name === "payableAmount") {
      currentProduct.payableAmount = Number(value) || 0;
      currentProduct.isManual = true;
    } else {
      currentProduct[name] = value;
      currentProduct.isManual = false;
    }

    if (name === "quantity" || name === "price") {
      const price = Number(currentProduct.price);
      const quantity = Number(currentProduct.quantity);
      if (!isNaN(price) && !isNaN(quantity)) {
        const total = price * quantity;
        currentProduct.totalAmount = total;

        if (!currentProduct.isManual) {
          currentProduct.payableAmount = total;
        }
      }
    }

    const price = Number(currentProduct.price);
    const payable = Number(currentProduct.payableAmount);
    const qty = Number(currentProduct.quantity);
    if (!isNaN(price) && !isNaN(payable)) {
      currentProduct.discount = price * qty - payable;
    }

    updated[index] = currentProduct;
    setProducts(updated);
    setErrors((prev) => ({ ...prev, [`product-${index}`]: null }));
  };

  const handleDimensionChange = (e) => {
    const { name, value } = e.target;
    setDimensions((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const calculateVolumetricWeight = () => {
    const { length, breadth, height } = dimensions;
    if (length && breadth && height) {
      return ((length * breadth * height) / 5000).toFixed(2);
    }
    return "";
  };

  const handleAddProduct = () => {
    setProducts((prev) => [
      ...prev,
      {
        productName: "",
        quantity: "",
        price: "",
        payableAmount: "",
        discount: "",
        totalAmount: "",
        sku: "",
        isManual: false,
      },
    ]);
  };

  const handleRemoveProduct = (index) => {
    setProducts(products.filter((_, i) => i !== index));
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handlePaymentSubmit = (data) => {
    console.log("💰 Payment Data Submitted:", data);
    setPaymentData(data);
    setErrors((prev) => ({ ...prev, paymentMethods: null }));
  };

  const handleSubmit = () => {
    const newErrors = {};

    products.forEach((p, index) => {
      if (!p.productName || !p.quantity || !p.price || !p.payableAmount) {
        newErrors[`product-${index}`] = "All product fields are required";
      }
    });

    ["weight", "length", "breadth", "height"].forEach((key) => {
      if (!dimensions[key]) {
        newErrors[key] = `${
          key.charAt(0).toUpperCase() + key.slice(1)
        } is required`;
      }
    });

    // Optional: Uncomment if payment is required
    // if (!paymentData.paymentMethods || paymentData.paymentMethods.length === 0) {
    //   newErrors.paymentMethods = "Please select at least one payment method and provide details";
    // }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      onNext({
        productInfo: products,
        dimensions: {
          ...dimensions,
          volumetricWeight: calculateVolumetricWeight(),
        },
        totalAmount,
        paymentData,
      });
    }
  };

  return (
    <div className="p-6 bg-white shadow rounded-lg">
      <h2 className="text-2xl font-semibold mb-6">Product Information</h2>

      {products.map((product, index) => (
        <div
          key={index}
          className="grid grid-cols-7 gap-4 mb-4 items-end border p-3 rounded"
        >
          <div>
            <label className="block mb-1 font-semibold">Product</label>
            <Select
              showSearch
              placeholder="Select Product"
              value={product.productName || undefined}
              optionLabelProp="label"
              dropdownMatchSelectWidth={false}
              dropdownStyle={{ minWidth: 250 }}
              onChange={(value) => {
                const updated = [...products];
                updated[index].productName = value;

                const selected = productOptions?.find((p) => p.name === value);
                if (selected) {
                  updated[index].price = selected.price;
                  updated[index].quantity = 1;
                  updated[index].payableAmount = selected.price;
                  updated[index].discount = 0;
                  updated[index].sku = selected.sku;
                }

                setProducts(updated);
                setErrors((prev) => ({ ...prev, [`product-${index}`]: null }));
              }}
              filterOption={(input, option) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
              className={`w-full ${
                errors[`product-${index}`] ? "border-red-500" : ""
              }`}
            >
              {productOptions?.map((prod) => (
                <Select.Option
                  key={prod._id}
                  value={prod.name}
                  label={prod.name}
                  title={prod.name}
                >
                  {prod.name}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div>
            <label className="block mb-1 font-semibold">Price</label>
            <input
              type="number"
              name="price"
              value={product.price}
              disabled
              className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100 text-gray-700"
            />
          </div>

          <div>
            <label className="block mb-1 font-semibold">Quantity</label>
            <input
              type="number"
              name="quantity"
              value={product.quantity}
              onChange={(e) => handleProductChange(index, e)}
              className={`w-full border rounded px-3 py-2 ${
                errors[`product-${index}`]
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
              min={1}
            />
          </div>

          <div>
            <label className="block mb-1 font-semibold">Total</label>
            <input
              type="number"
              value={product.price * product.quantity || 0}
              readOnly
              className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100"
            />
          </div>

          <div>
            <label className="block mb-1 font-semibold">Net Payable</label>
            <input
              type="number"
              name="payableAmount"
              value={product.payableAmount}
              onChange={(e) => handleProductChange(index, e)}
              className={`w-full border rounded px-3 py-2 ${
                errors[`product-${index}`]
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
            />
          </div>

          <div>
            <label className="block mb-1 font-semibold">Discount</label>
            <input
              type="number"
              value={product.discount}
              readOnly
              className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100"
            />
          </div>

          {errors[`product-${index}`] && (
            <div className="col-span-6 text-red-500 text-sm mt-1">
              {errors[`product-${index}`]}
            </div>
          )}

          <div className="flex space-x-2 justify-center align-center text-center mt-2">
            <button
              onClick={handleAddProduct}
              className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
              type="button"
            >
              +
            </button>
            {products.length > 1 && (
              <button
                onClick={() => handleRemoveProduct(index)}
                className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                type="button"
              >
                -
              </button>
            )}
          </div>
        </div>
      ))}

      <div className="grid grid-cols-4 gap-4 mt-6">
        <div>
          <label className="block mb-1 font-semibold">Total Items</label>
          <input
            type="number"
            value={products.reduce(
              (total, product) => total + Number(product.quantity || 0),
              0
            )}
            readOnly
            className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100"
          />
        </div>
        <div>
          <label className="block mb-1 font-semibold">Total Price (₹)</label>
          <input
            type="number"
            value={totalPrice}
            readOnly
            className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100"
          />
        </div>
        <div>
          <label className="block mb-1 font-semibold">
            Payable Amount / Price (₹)
          </label>
          <input
            type="number"
            value={totalAmount}
            onChange={(e) => {
              setTotalAmount(Number(e.target.value) || 0);
              setIsManualTotal(true);
            }}
            className="w-full border border-gray-300 rounded px-3 py-2"
          />
        </div>
        <div>
          <label className="block mb-1 font-semibold">Total Discount (₹)</label>
          <input
            type="number"
            value={totalDiscount}
            readOnly
            className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100"
          />
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4 mt-6">
        {["weight", "length", "breadth", "height"].map((key) => (
          <div key={key}>
            <label className="block mb-1 font-semibold">
              {key.charAt(0).toUpperCase() + key.slice(1)}{" "}
              {key === "weight" ? "(kg)" : "(cm)"}
            </label>
            <input
              type="number"
              name={key}
              value={dimensions[key]}
              onChange={handleDimensionChange}
              className={`w-full border rounded px-3 py-2 ${
                errors[key] ? "border-red-500" : "border-gray-300"
              }`}
            />
            {errors[key] && (
              <p className="text-red-500 text-sm mt-1">{errors[key]}</p>
            )}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-4 mt-6">
        <div>
          <label className="block mb-1 font-semibold">
            Volumetric Weight (kg)
          </label>
          <input
            type="text"
            value={calculateVolumetricWeight()}
            readOnly
            className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100"
          />
        </div>

        <div>
          <label className="block mb-2 font-semibold">Payment Option</label>
          <button
            type="button"
            onClick={handleOpenModal}
            className="w-full py-2 px-4 rounded border border-gray-300 bg-white text-gray-700 hover:border-blue-500 hover:bg-blue-50 transition-colors"
          >
            {paymentData.paymentMethods && paymentData.paymentMethods.length > 0
              ? `${paymentData.paymentMethods.length} Payment Method(s) Selected - Click to Edit`
              : "Select Payment Methods"}
          </button>
          {errors.paymentMethods && (
            <p className="text-red-500 text-sm mt-1">{errors.paymentMethods}</p>
          )}
        </div>
      </div>

      {paymentData.paymentMethods && paymentData.paymentMethods.length > 0 && (
        <div className="mt-6 p-4 bg-green-50 border border-green-300 rounded-lg">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold text-gray-800">Payment Summary</h3>
            <button
              onClick={handleOpenModal}
              className="text-sm text-blue-600 hover:text-blue-800 underline"
            >
              Edit Payment Details
            </button>
          </div>
          <div className="space-y-2">
            {paymentData.paymentMethods.map((method, index) => (
              <div key={index} className="flex justify-between text-sm">
                <span className="text-gray-700">{method.paymentOption}:</span>
                <span className="font-medium text-gray-900">
                  ₹ {method.paymentAmount}
                </span>
              </div>
            ))}
          </div>
          <div className="border-t border-green-300 mt-3 pt-3 flex justify-between">
            <span className="font-semibold text-gray-800">Total Payment:</span>
            <span className="text-lg font-bold text-green-600">
              ₹ {paymentData.totalAmount}
            </span>
          </div>
        </div>
      )}

      <div className="flex justify-end space-x-4 mt-6">
        <button
          onClick={onPrevious}
          className="bg-primaryLight border hover:border-primary border-gray-300 hover:text-primary text-gray-600 px-6 py-2 rounded"
        >
          Previous
        </button>
        <button
          disabled={isLoading}
          onClick={handleSubmit}
          className={`px-6 py-2 rounded text-white bg-primary hover:text-primary hover:bg-primaryLight border border-primary ${
            isLoading ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          {isLoading ? "Creating..." : "Submit"}
        </button>
      </div>

      <PaymentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handlePaymentSubmit}
        existingPaymentData={paymentData}
      />
    </div>
  );
};

export default ProductInformation;
